import { trackManualCaptureStop } from '@/lib/desktopExitCaptureStop';
import { isOfflineBridgeError } from '@/tauri/offline-recording/errors';
import { offlineRecordingBridge } from '@/tauri/offline-recording/recording';
import { logger } from '@/utils/logger/logger';
import { bridgeErrorKind } from '@heidi/plugin-core';
import { activeSessionIdAtom } from '@heidi/session/data';
import { getDefaultStore } from 'jotai';

// The budget sums Rust's producer join, writer join, and finalize headroom.
// It detects a wedge, not a schedule; the writer-join mirror is pinned to Rust by
// the native-budget test.
const NATIVE_PRODUCER_JOIN_MS = 2_000;
export const NATIVE_WRITER_JOIN_MIRROR_MS = 47_000;
const NATIVE_FINALIZE_HEADROOM_MS = 30_000;
export const OFFLINE_CAPTURE_STOP_TIMEOUT_MS =
  NATIVE_PRODUCER_JOIN_MS + NATIVE_WRITER_JOIN_MIRROR_MS + NATIVE_FINALIZE_HEADROOM_MS;

const offlineCaptureStopOwners = new Map<string, Set<symbol>>();

export function trackOfflineCaptureStop(id: string): () => void {
  const token = Symbol();
  const owners = offlineCaptureStopOwners.get(id) ?? new Set<symbol>();
  owners.add(token);
  offlineCaptureStopOwners.set(id, owners);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    owners.delete(token);
    if (owners.size === 0 && offlineCaptureStopOwners.get(id) === owners) {
      offlineCaptureStopOwners.delete(id);
    }
  };
}

export function isOfflineCaptureStopInFlight(id: string): boolean {
  return offlineCaptureStopOwners.has(id);
}

export async function stopActiveOfflineCapture(
  activeCaptureId: string | null,
  callSite: 'closeGuard' | 'logout',
  boundMs = OFFLINE_CAPTURE_STOP_TIMEOUT_MS
): Promise<'settled' | 'timedOut' | 'failed'> {
  if (!activeCaptureId) return 'settled';

  const isCloseGuard = callSite === 'closeGuard';
  if (isCloseGuard) {
    logger.info('[close-guard] Finalizing offline capture before close', {
      localSessionId: activeCaptureId,
    });
  } else {
    logger.info('[offline-capture-stop] Finalizing offline capture before logout');
  }
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const sessionId = getDefaultStore().get(activeSessionIdAtom);
  const releaseManualStop = sessionId ? trackManualCaptureStop(sessionId) : undefined;
  const releaseOfflineStop = trackOfflineCaptureStop(activeCaptureId);
  try {
    const stopPromise = Promise.resolve()
      .then(() => offlineRecordingBridge.stop(activeCaptureId))
      .then(() => 'settled' as const)
      .catch((error) => {
        if (
          isOfflineBridgeError(error, 'OFFLINE_RECORD_NOT_FOUND', 'Recording not found')
        ) {
          logger.info('[offline-capture-stop] Capture was already finalized', {
            errorKind: bridgeErrorKind(error),
          });
          return 'settled' as const;
        }
        logger.error(
          isCloseGuard
            ? '[close-guard] Offline capture stop failed before close'
            : '[offline-capture-stop] Native stop failed',
          { errorKind: bridgeErrorKind(error) }
        );
        return 'failed' as const;
      })
      .finally(() => {
        releaseOfflineStop();
        releaseManualStop?.();
      });
    return await Promise.race([
      stopPromise,
      new Promise<'timedOut'>((resolve) => {
        timeoutId = setTimeout(
          () => {
            logger.error(
              isCloseGuard
                ? '[close-guard] Offline capture stop did not settle in time — closing anyway'
                : '[offline-capture-stop] Native stop exceeded its time bound'
            );
            resolve('timedOut');
          },
          Math.max(0, boundMs)
        );
      }),
    ]);
  } catch (error) {
    logger.error(
      isCloseGuard
        ? '[close-guard] Offline capture stop failed before close'
        : '[offline-capture-stop] Native stop failed',
      { errorKind: bridgeErrorKind(error) }
    );
    return 'failed';
  } finally {
    clearTimeout(timeoutId);
    if (!timeoutId) {
      releaseOfflineStop();
      releaseManualStop?.();
    }
  }
}
